import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConnectorDetails, useTriggerExtraction, useDeleteConnector, useCancelExecution } from '@/hooks/useConnectors';
import { readLastLibraryUrl } from '@/lib/lastLibraryUrl';
import { ConnectorHeader } from '@/components/connectors/detail/ConnectorHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Clock, Settings } from 'lucide-react';
import { useConnectorDetailPageState } from '@/hooks/useConnectorDetailPageState';
import { ConfigurationTab } from '@/components/connectors/detail/ConfigurationTab';
import { ActivityTab } from '@/components/connectors/detail/ActivityTab';
import { StopRunDialog } from '@/components/connectors/detail/StopRunDialog';
import { useToast } from '@/hooks/useToast';
import { ScheduleTab as DetailScheduleTab } from '@/components/connectors/detail/ScheduleTab';

export function ConnectorDetailPage() {
  const { connectorType, name: nameRaw } = useParams<{ connectorType: string; name: string }>();
  const name = nameRaw ? decodeURIComponent(nameRaw) : '';
  const { data, isLoading, error } = useConnectorDetails(connectorType ?? '', name);
  const { tab, setTab, setExecutionId } = useConnectorDetailPageState();
  const [stopOpen, setStopOpen] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [optimisticRunning, setOptimisticRunning] = useState(false);
  const startingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerMut = useTriggerExtraction();
  const deleteMut = useDeleteConnector();
  const cancelMut = useCancelExecution();
  const { toast } = useToast();
  const navigate = useNavigate();

  const connector = data?.connector;
  const latestStatus = connector?.latest_execution?.status;

  // Reconcile the optimistic "Starting…" state: once the real execution shows
  // pending/running, the Stop button takes over, so drop the optimistic flag.
  // Adjusted during render (not an effect) via a previous-value mirror of
  // `latestStatus` (a stable string primitive): converges on the very next
  // render, so this fires exactly once per status change.
  const [prevLatestStatus, setPrevLatestStatus] = useState(latestStatus);
  if (latestStatus !== prevLatestStatus) {
    setPrevLatestStatus(latestStatus);
    if (latestStatus === 'pending' || latestStatus === 'running') {
      setOptimisticRunning(false);
    }
  }

  // Clear the safety timer on unmount.
  useEffect(() => () => {
    if (startingTimer.current) clearTimeout(startingTimer.current);
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (error || !connector) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>Connector not found.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const onRunNow = async () => {
    // Optimistically flip the button to a disabled "Starting…" the instant it's
    // clicked, before the network round-trip — this hard-blocks a double-submit
    // during the window before the detail refetch surfaces the running status.
    setOptimisticRunning(true);
    if (startingTimer.current) clearTimeout(startingTimer.current);
    // Safety net: if the run never reaches pending/running (e.g. a lock skip),
    // revert Starting… back to Run Now instead of sticking indefinitely.
    startingTimer.current = setTimeout(() => setOptimisticRunning(false), 12000);
    try {
      const result = await triggerMut.mutateAsync({
        connectorType: connector.connectorType as any,
        configurationId: connector.id ? String(connector.id) : undefined,
      });
      toast({ title: 'Triggered', description: `Run started for "${connector.name}".` });
      // Switch to Activity and select the new execution. The row may not be
      // in the executionHistory cache yet (the trigger creates it server-side
      // before the response, and the existing useTriggerExtraction.onSuccess
      // invalidations refetch the list shortly after); ActivityTab will pick
      // up the selection once the row appears.
      setTab('activity');
      if (result?.execution_id) {
        setExecutionId(result.execution_id);
      }
    } catch (err) {
      setOptimisticRunning(false);
      toast({
        title: 'Run failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };
  const onStopRequested = () => setStopOpen(true);
  const onStopConfirm = async () => {
    const id = connector.latest_execution?.execution_id;
    if (!id) return;
    setIsStopping(true);
    setStopOpen(false);
    try {
      await cancelMut.mutateAsync(id);
    } catch (err) {
      const message =
        (err as any)?.response?.data?.detail ??
        (err instanceof Error ? err.message : String(err));
      toast({ title: 'Failed to stop run', description: message, variant: 'destructive' });
    } finally {
      setIsStopping(false);
    }
  };
  const onDelete = async () => {
    if (!connector.id) {
      toast({
        title: 'Cannot delete',
        description: 'This connector has no configuration ID.',
        variant: 'destructive',
      });
      return;
    }
    if (!confirm(`Delete connector "${connector.name}"?`)) return;
    try {
      await deleteMut.mutateAsync({ configId: connector.id });
      toast({ title: 'Deleted', description: `"${connector.name}" removed.` });
      navigate(readLastLibraryUrl());
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <ConnectorHeader
        connector={connector as any}
        isStopping={isStopping}
        isTriggering={triggerMut.isPending || optimisticRunning}
        onRunNow={onRunNow}
        onStopRequested={onStopRequested}
        onDelete={onDelete}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="activity"><LineChart className="mr-2 h-4 w-4" aria-hidden />Activity</TabsTrigger>
          <TabsTrigger value="schedule"><Clock className="mr-2 h-4 w-4" aria-hidden />Schedule</TabsTrigger>
          <TabsTrigger value="configuration"><Settings className="mr-2 h-4 w-4" aria-hidden />Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <ActivityTab connector={connector as any} />
        </TabsContent>
        <TabsContent value="schedule">
          <DetailScheduleTab connectorType={connector.connectorType} name={connector.name} />
        </TabsContent>
        <TabsContent value="configuration">
          <ConfigurationTab connectorType={connector.connectorType} name={connector.name} />
        </TabsContent>
      </Tabs>
      <StopRunDialog open={stopOpen} onOpenChange={setStopOpen} onConfirm={onStopConfirm} />
    </div>
  );
}
